import { Component } from '@theme/component';

const STORAGE_TTL_DAYS = 14;

/**
 * Promo popup with delayed show and localStorage dismiss expiry.
 *
 * @extends {Component}
 */
class PromoPopupComponent extends Component {
  /** @type {number | undefined} */
  #timer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {MediaQueryList | undefined} */
  #desktopQuery;

  connectedCallback() {
    super.connectedCallback();
    this.#bindScrollbarOffset();

    if (this.querySelector('[data-promo-popup-success]')) {
      this.#persist();
      this.#show();
      return;
    }

    if (this.#isDismissed()) {
      this.#hide();
      return;
    }

    if (this.#isDesignMode()) {
      this.#show();
      return;
    }

    const delayMs = Number.parseInt(this.dataset.delay || '5', 10) * 1000;
    this.#timer = window.setTimeout(() => this.#show(), Math.max(delayMs, 0));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) window.clearTimeout(this.#timer);
    this.#unbindScrollbarOffset();
  }

  dismiss() {
    this.#persist();
    this.#hide();
  }

  #show() {
    this.hidden = false;
    this.setAttribute('data-open', '');
  }

  #hide() {
    this.hidden = true;
    this.removeAttribute('data-open');
  }

  #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  #storageKey() {
    return this.dataset.storageKey || 'promo-popup';
  }

  #isDismissed() {
    try {
      const raw = window.localStorage.getItem(this.#storageKey());
      if (!raw) return false;

      const parsed = JSON.parse(raw);
      const expiresAt = Number(parsed?.expiresAt);
      if (!Number.isFinite(expiresAt)) return false;
      if (Date.now() > expiresAt) {
        window.localStorage.removeItem(this.#storageKey());
        return false;
      }
      return Boolean(parsed?.dismissed);
    } catch {
      return false;
    }
  }

  #onScrollbarChange = () => {
    this.#syncScrollbarOffset();
  };

  #scroller() {
    if (this.#desktopQuery?.matches) {
      const wrapper = document.querySelector('.page-wrapper');
      if (wrapper instanceof HTMLElement) return wrapper;
    }

    return document.documentElement;
  }

  /**
   * Offset by the live scrollbar width so the popup never sits on the page bar.
   * Overlay scrollbars measure as 0; classic/thin bars (including the 20px WebKit bar) do not.
   */
  #syncScrollbarOffset() {
    const scroller = this.#scroller();
    let width = Math.max(0, scroller.offsetWidth - scroller.clientWidth);

    if (width === 0) {
      width = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    }

    this.style.setProperty('--page-scrollbar-width', `${Math.round(width)}px`);
  }

  #bindScrollbarOffset() {
    this.#desktopQuery = window.matchMedia('(min-width: 992px)');
    this.#syncScrollbarOffset();
    window.addEventListener('resize', this.#onScrollbarChange);
    this.#desktopQuery.addEventListener('change', this.#onScrollbarChange);

    const scroller = document.querySelector('.page-wrapper');
    if (scroller instanceof HTMLElement && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#onScrollbarChange);
      this.#resizeObserver.observe(scroller);
    }
  }

  #unbindScrollbarOffset() {
    window.removeEventListener('resize', this.#onScrollbarChange);
    this.#desktopQuery?.removeEventListener('change', this.#onScrollbarChange);
    this.#resizeObserver?.disconnect();
  }

  #persist() {
    if (this.#isDesignMode()) return;

    try {
      const expiresAt = Date.now() + STORAGE_TTL_DAYS * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(
        this.#storageKey(),
        JSON.stringify({ dismissed: true, expiresAt })
      );
    } catch {
      // Ignore private-mode / blocked storage.
    }
  }
}

if (!customElements.get('promo-popup-component')) {
  customElements.define('promo-popup-component', PromoPopupComponent);
}
